import { PATH } from '@/router/PATH';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type OperationsTabId = 'templates' | 'record' | 'activity';

export type OperationsTab = {
    id: OperationsTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
};

/**
 * The operations tabs, in the order the work happens in: configure the
 * operation, record one, then look at what was recorded.
 *
 * The first is the default — the bare `/accounting/operations` renders it
 * through an index route, so the tab bar reads the same whether the URL carries
 * the `templates` segment or not.
 *
 * No `roles` constraint on any of them, unlike `registersTabsMeta`. Salaries and
 * Dividends are hidden from a BranchAdmin because every one of their endpoints
 * is OrgAdmin and the tab could only 403; the operation catalog is readable by
 * both admin roles, so all three tabs are genuinely available to everyone who
 * reached this shell. What differs by role is *editing*, which is
 * `operationsAccess`'s call and belongs on the affordance, not on the tab.
 */
export const OPERATIONS_TABS: OperationsTab[] = [
    {
        id: 'templates',
        path: PATH.ACCOUNTING.OPERATIONS.TEMPLATES,
        labelKey: 'accounting.operations.tabs.templates',
    },
    {
        id: 'record',
        path: PATH.ACCOUNTING.OPERATIONS.RECORD,
        labelKey: 'accounting.operations.tabs.record',
    },
    {
        id: 'activity',
        path: PATH.ACCOUNTING.OPERATIONS.ACTIVITY,
        labelKey: 'accounting.operations.tabs.activity',
    },
];

export const DEFAULT_OPERATIONS_TAB_ID: OperationsTabId = 'templates';

/**
 * Which tab the current URL selects. The bare shell path and anything
 * unrecognised fall back to the default tab, so the bar is never rendered with
 * nothing selected.
 */
export const resolveOperationsTabId = (pathname: string): OperationsTabId => {
    const matched = OPERATIONS_TABS.find(
        (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
    );

    return matched?.id ?? DEFAULT_OPERATIONS_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveOperationsTabPath = (id: string): string => {
    const matched = OPERATIONS_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.ACCOUNTING.OPERATIONS.TEMPLATES;
};
